import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { FaceRecognitionStatusEnum } from '@goldcast/api/content';
import { isNumber } from 'radash';
import { useAppContext } from '../AppContext/AppContext';
import {
  getLayoutAvailabilityStatus,
  hasShownScreenshareToast,
  markScreenshareToastAsShown,
  SCREENSHARE_TOAST_MESSAGE
} from './ClipsContextUtils';
import ClipSkeletonLoader from './ClipSkeletonLoader/ClipSkeletonLoader';
import EventMigrationInProgress from './PlayerBlockers/EventMigrationInProgress';
import OriginalRecordingGenerationInProgress from './PlayerBlockers/OriginalRecordingGenerationInProgress';
import { ClipUnavailableReason } from '@/Pages/Clip/MagicLayout/constants';
import {
  clipCaptionsLoader,
  currentClip,
  initClip,
  saveClipChanges,
  clipsNeedingConfigUpdateStore,
  updateClipInList,
  updateClipWithId,
  isScreenShareDetectionFeatureFlagEnabled,
  isFullVideoFaceRecEnabled,
  isLayoutsEnabledForPreRecordedEventsFeatureFlag
} from '@/stores/clip';
import { Clip } from '@/player-config/src/types/clip-types';
import { core, initContent, initSharedContent } from '@/stores/core';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import {
  BaseSpeakerAnalysisOptions,
  defaultSpeakersAnalysis,
  getSpeakersAnalysis
} from '@/player-config/src/retrievers/speakerAnalysisApi';
import {
  loadClipBrandKit,
  loadFtuxBrandKitFonts,
  loadPlatformBrandKits,
  loadSharedBrandKitData
} from '@/stores/brandKit';
import { SpeakersAnalysis } from '@/player-config/src/types/speakers-analysis';
import { SharedAPIStore, initSharedAPI, sharedAPI, updateClipConfig } from '@/stores/sharedAPI/sharedAPI';
import { ClipPlayerStore, defaultClipPlayerStore, initPlayerV2, playerV2 } from '@/stores/playerV2';
import { defaultSharedAPIStore } from '@/stores/sharedAPI/constants';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { doesClipHavePlayerConfig, getClipBroadcastId, getClipFPS } from '@/player-config/src/utils/clipUtils';
import { getAlignedRecordings } from '@/player-config/src/utils/alignedRecordings';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { getFallbackFPS } from '@/player-config/src/utils/fpsUtils';
import { aiEditsStore } from '@/stores/aiEditsStore';
import Loader from '@/components/atoms/Loader';
import useAnalytics from '@/hooks/useAnalytics';
import RSClipFallbackWithoutNewTimeline from '@/context/ClipsContext/PlayerBlockers/RSClipFallbackWithoutNewTimeline';
import { usePusherContext } from '@/context/PusherContext/PusherContext';
import { PUSHER_STATES } from '@/context/PusherContext/PusherContextConstants';
import { useAIEditsAdapter } from '@/hooks/useAIEditsAdapter';
import type { AIEditsAdapter } from '@/adapters/aiEdits/AIEditsAdapter';
import { useClipAvailabilityStatus } from '@/hooks/useClipAvailabilityStatus';
import { ClipAvailabilityStatus } from '@/player-config/src/types/clip-availability';
import { showSuccessToast } from '@/libs/toast/toast';
import { selectSpeakerAnalysis } from '@/player-config/src/selectors/contentMetadataStoreSelectors';

const ActionsContext = createContext<{
  setClipVideosLoading: (loading: boolean) => void;
  setIsClipAllowedToPlay: (loading: boolean) => void;
}>({
  setClipVideosLoading: () => {},
  setIsClipAllowedToPlay: () => {}
});

export const ClipsContext = createContext<{
  clipId: string;
  clipData: Clip;
  clipVideosLoading: boolean;
  clipCaptionsLoading: boolean;
  speakersAnalysis: SpeakersAnalysis;
  sharedAPIStore: SharedAPIStore;
  playerStore: ClipPlayerStore;
  layoutStatus: FaceRecognitionStatusEnum | null;
  // This is one of those states to use to clear the block on the UI
  isClipAllowedToPlay: boolean;
  fps: number;
  aiEditsAdapter: AIEditsAdapter | null;
}>({
  clipId: '',
  clipData: {} as Clip,
  clipVideosLoading: true,
  clipCaptionsLoading: false,
  speakersAnalysis: defaultSpeakersAnalysis,
  sharedAPIStore: defaultSharedAPIStore,
  playerStore: defaultClipPlayerStore,
  layoutStatus: null,
  isClipAllowedToPlay: false,
  fps: getFallbackFPS(),
  aiEditsAdapter: null
});

export type ClipsContextError = { message: string; statusCode?: number };

export const ClipsContextProvider = ({
  clipId,
  children,
  renderErrorComponent,
  shared = false,
  showLoaderOnPage = false,
  isSavedSearchPage = false,
  isSmartEventsPage = false,
  useNewSkeletonLoader = false,
  parentContext
}: {
  clipId?: string;
  children: React.ReactNode;
  renderErrorComponent?: (error: ClipsContextError, clipId: string) => JSX.Element;
  shared?: boolean;
  /** Show full page loader when loading */
  showLoaderOnPage?: boolean;
  isSavedSearchPage?: boolean;
  isSmartEventsPage?: boolean;
  useNewSkeletonLoader?: boolean;
  parentContext?: string;
}) => {
  const { logger } = useAppContext();
  const { trackClipLoadTime, trackClipOpenedWithAsdCompleted } = useAnalytics();
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const [error, setError] = useState<ClipsContextError | null>(null);

  const [loading, setLoading] = useState(true);
  const [clipVideosLoading, setClipVideosLoading] = useState(true);
  const [isClipAllowedToPlay, setIsClipAllowedToPlay] = useState(false);
  const [layoutStatus, setLayoutStatus] = useState<FaceRecognitionStatusEnum | null>(null);

  const clipLoadTime = useRef<Record<string, number>>({});

  const allClips = useSyncExternalStore(currentClip.subscribe, currentClip.getSnapshot);
  const clipData = allClips[clipId!] || {};

  const speakersAnalysis = useContentMetadataStore(selectSpeakerAnalysis(getClipBroadcastId(clipData) ?? ''));

  const sharedAPIStoreForAllClips = useSyncExternalStore(sharedAPI.subscribe, sharedAPI.getSnapshot);
  const sharedAPIStore = sharedAPIStoreForAllClips[clipId!];

  const playerStoreForAllClips = useSyncExternalStore(playerV2.subscribe, playerV2.getSnapshot);
  const playerStore = playerStoreForAllClips[clipId!];

  const clipCaptionsLoaderState = useSyncExternalStore(clipCaptionsLoader.subscribe, clipCaptionsLoader.getSnapshot);
  const clipCaptionsLoading = clipCaptionsLoaderState[clipId!] || false;

  const clipsNeedingConfigUpdate = useSyncExternalStore(
    clipsNeedingConfigUpdateStore.subscribe,
    clipsNeedingConfigUpdateStore.getSnapshot
  );

  const { orgChannel } = usePusherContext();

  const { adapter: aiEditsAdapter, buildContext } = useAIEditsAdapter({
    clipId,
    orgChannel
  });

  const aiEditsState = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);

  const clipBroadcastId = useMemo(() => getClipBroadcastId(clipData), [clipData]);

  useEffect(() => {
    if (clipId && parentContext) {
      if (loading) {
        clipLoadTime.current[clipId] = performance.now();
      } else if (isNumber(clipLoadTime.current[clipId])) {
        const timeTaken = performance.now() - clipLoadTime.current[clipId];
        trackClipLoadTime({ clipId, loadTime: timeTaken, context: parentContext });
      }
    }
  }, [loading, clipId, trackClipLoadTime, parentContext]);

  useEffect(() => {
    return () => {
      if (clipId) {
        initSharedAPI(clipId);
      }
    };
  }, []);

  useEffect(() => {
    if (!clipBroadcastId) {
      return;
    }
    return () => {
      if (
        clipBroadcastId &&
        getAlignedRecordings({ broadcastId: clipBroadcastId, contentMetadataStoreInstance: useContentMetadataStore })
          .length !== 0
      ) {
        useContentMetadataStore.getState().updateAlignedRecording({
          contentId: clipBroadcastId,
          alignedRecordings: [],
          areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
        });
      }
    };
  }, [clipBroadcastId]);

  useEffect(() => {
    if (!clipId || !aiEditsAdapter || !buildContext) {
      return;
    }

    const POLL_INTERVAL = 20000;
    const MAX_POLL_DURATION = 8 * 60 * 1000;

    let pollInterval: NodeJS.Timeout | null = null;
    let timeoutTimer: NodeJS.Timeout | null = null;

    const isProcessing = aiEditsState.agentStatus.state === 'Processing';

    const pollStatus = async () => {
      const context = buildContext();
      if (!context) {
        return;
      }

      try {
        const status = await aiEditsAdapter.getStatus(context);
        EventBus.dispatch(CustomEvents.AIAgentStatusUpdated, status);
      } catch (error) {
        logger.error(`Error polling AI agent status for clipId: ${clipId}`, error);
      }
    };

    if (isProcessing && !pollInterval) {
      logger.info(`Starting AI agent status polling for clipId: ${clipId}`);
      pollInterval = setInterval(pollStatus, POLL_INTERVAL);

      timeoutTimer = setTimeout(() => {
        logger.warn(`AI agent status polling timeout reached for clipId: ${clipId}`);
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }, MAX_POLL_DURATION);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
      }
    };
  }, [clipId, aiEditsAdapter, buildContext, aiEditsState.agentStatus.state, logger, aiEditsState.agentStatus]);

  const isSpeakerLayout = useMemo(
    () =>
      clipData.asset_metadata?.layout &&
      (clipData.asset_metadata.layout === 'SPEAKER' || clipData.asset_metadata.layout === 'GRID'),
    [clipData.asset_metadata?.layout]
  );

  const initData = useCallback(
    async ({
      id,
      shouldReloadClip,
      updateCoreStore
    }: {
      id: string;
      shouldReloadClip?: boolean;
      updateCoreStore?: boolean;
    }) => {
      sessionStorage.removeItem(`facialRecognitionStatusAndTypeFor:${id}`);
      try {
        if (!shouldReloadClip) {
          initSharedAPI(id);
          initPlayerV2(id);
        }

        const clip = await initClip(id, { shared, isSavedSearchPage, isSmartEventsPage });

        // Make sure to update clip in list so edit captions works in clip list
        updateClipInList(clip);

        if (shared) {
          loadSharedBrandKitData(
            clip.id,
            clip.asset_metadata.caption_styles?.font_location || clip.asset_metadata.font_location
          );
        } else {
          const featureFlags = featureFlagStore.getSnapshot();
          await loadClipBrandKit(id);
          if (featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit]) {
            await loadPlatformBrandKits();
            await loadFtuxBrandKitFonts(getClipBroadcastId(clip)!);
          }
        }

        setLayoutStatus(
          getLayoutAvailabilityStatus(
            clip.asset_metadata.layout,
            clip.layout_status,
            clip,
            !shared || doesClipHavePlayerConfig(clip)
          )
        );

        if (coreStore.content?.id !== clip.content.id || updateCoreStore) {
          try {
            const contentPromise = shared ? initSharedContent : initContent;

            await contentPromise(clip.broadcast);
          } catch (err: any) {
            if (err?.statusCode === 404 || err?.statusCode === 401) {
              setError(err);
            } else {
              logger.error('Error initializing content', err);
              setError({ message: 'Something went wrong on our end' });
            }
          }
        }
      } catch (err: any) {
        if (err?.statusCode === 404 || err?.statusCode === 401) {
          setError(err);
        } else {
          logger.error('Error fetching clip', err);
          setError(err);
        }
      }

      startTransition(() => {
        setLoading(false);
        if (clipId) {
          useContentMetadataStore.getState().setClipDataLoaded({
            contentId: clipId,
            isLoaded: false,
            areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
          });
        }
      });
    },
    []
  );

  useEffect(() => {
    if (!clipId) {
      setError({ message: 'Clip ID not found', statusCode: 404 });
      return;
    }

    setLoading(true);
    useContentMetadataStore.getState().setClipDataLoaded({
      contentId: clipId,
      isLoaded: true,
      areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
    });
    initData({ id: clipId });
  }, [clipId]);

  function reloadClip({
    cb,
    showLoader,
    assetId,
    updateCoreStore
  }: { cb?: () => void; showLoader?: boolean; assetId?: string; updateCoreStore?: boolean } = {}) {
    if (showLoader) {
      setLoading(true);
    }
    if (assetId && clipId !== assetId) {
      initData({ id: assetId!, shouldReloadClip: true, updateCoreStore }).then(() => {
        cb?.();
      });
      return;
    }
    initData({ id: clipId!, shouldReloadClip: true, updateCoreStore }).then(() => {
      cb?.();
    });
  }

  const updateClipAndLayoutStatus = useCallback(
    (clipId: string, status: FaceRecognitionStatusEnum, isAnalysisAccurate: boolean) => {
      const newClipLayoutStatus = {
        ...clipData.layout_status,
        SPEAKER: {
          ...clipData.layout_status['SPEAKER'],
          status,
          accurate: isAnalysisAccurate
        },
        GRID: {
          ...clipData.layout_status['GRID'],
          status,
          accurate: isAnalysisAccurate
        }
      };

      // If block, can be removed after removal of Use_CL_Remotion_For_Custom_Uploads_Clips
      if (isSpeakerLayout) {
        setLayoutStatus(
          getLayoutAvailabilityStatus(
            clipData.asset_metadata.layout,
            newClipLayoutStatus,
            clipData,
            !shared || doesClipHavePlayerConfig(clipData)
          )
        );
      }

      if (status === 'DONE' || status === 'FAILED') {
        const isFullVideoFaceRec = isFullVideoFaceRecEnabled();
        const baseOptions: BaseSpeakerAnalysisOptions = {
          clipId,
          clip: clipData,
          onAsdComplete: () => trackClipOpenedWithAsdCompleted({ clipId }),
          isLayoutEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag(),
          token: window.accessToken,
          contentMetadataStoreInstance: useContentMetadataStore,
          isScreenshareDetectionEnabled: isScreenShareDetectionFeatureFlagEnabled()
        };
        getSpeakersAnalysis(
          isFullVideoFaceRec
            ? {
                ...baseOptions,
                contentId: clipBroadcastId!
              }
            : {
                ...baseOptions,
                start: clipData.asset_metadata.start
              }
        )
          .catch(() => {
            if (!clipBroadcastId) {
              return;
            }
            const speakerAnalysis = useContentMetadataStore.getState().data[clipBroadcastId].metadata.speakerAnalysis;
            if (speakerAnalysis && speakerAnalysis.times.length !== 0) {
              if (clipBroadcastId) {
                useContentMetadataStore.getState().updateSpeakerAnalysis({
                  contentId: clipBroadcastId,
                  speakerAnalysis: defaultSpeakersAnalysis,
                  areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag()
                });
              }
            }
          })
          .finally(() => {
            updateClipWithId(clipId, {
              layout_status: newClipLayoutStatus
            });

            saveClipChanges({
              clipId,
              shouldGenerateThumbnail: false,
              shouldAddToHistory: false,
              isEditedState: false
            });

            EventBus.dispatch(CustomEvents.ReloadTemplates);
          });
      }
    },
    [clipData, isSpeakerLayout]
  );

  useEffect(() => {
    if (!clipId || !clipData.asset_metadata || loading) {
      return;
    }

    const facialRecognitionStatusAndType = sessionStorage.getItem(`facialRecognitionStatusAndTypeFor:${clipId}`);
    if (facialRecognitionStatusAndType) {
      const [status, isAnalysisAccurate] = facialRecognitionStatusAndType.split('-');
      sessionStorage.removeItem(`facialRecognitionStatusAndTypeFor:${clipId}`);
      updateClipAndLayoutStatus(clipId, status as FaceRecognitionStatusEnum, isAnalysisAccurate === 'true');
    } else {
      setLayoutStatus(
        getLayoutAvailabilityStatus(
          clipData.asset_metadata.layout,
          clipData.layout_status,
          clipData,
          !shared || doesClipHavePlayerConfig(clipData)
        )
      );
    }
  }, [clipData.asset_metadata?.layout, loading]);

  // Set speakersAnalysis.loading to false when layout is UNAVAILABLE
  useEffect(() => {
    if (!clipId || !layoutStatus) {
      return;
    }
    const clipLayout = clipData?.asset_metadata?.layout;
    const layoutStatusReason = clipLayout && clipData?.layout_status?.[clipLayout]?.reason;

    if (
      layoutStatus === 'UNAVAILABLE' &&
      layoutStatusReason === ClipUnavailableReason.UNAVAILABLE_FOR_CLIPS_LONGER_THAN_THRESHOLD
    ) {
      if (clipBroadcastId) {
        useContentMetadataStore.getState().setSpeakerAnalysisLoading(clipBroadcastId, false);
      }
    }
  }, [clipId, layoutStatus, clipData?.asset_metadata?.layout, clipData?.layout_status]);

  useEffect(() => {
    if (!clipId || !Object.keys(clipData).length) {
      return;
    }

    if (loading) {
      return;
    }

    if (
      coreStore.content?.media_type === 'VIDEO' &&
      coreStore.content?.media_source_type === 'UPLOAD' &&
      isSpeakerLayout &&
      (!speakersAnalysis || speakersAnalysis.loading)
    ) {
      return;
    }

    if (!clipsNeedingConfigUpdate[clipId] || shared) {
      return;
    }

    updateClipConfig({ clipId, clip: allClips[clipId] }).then(value => {
      updateClipWithId(clipId, { updated_at: value.updated_at }, false, true);
    });
    clipsNeedingConfigUpdateStore.update(data => ({ ...data, [clipId]: false }));
  }, [
    clipId,
    isSpeakerLayout,
    speakersAnalysis,
    allClips,
    loading,
    clipsNeedingConfigUpdate,
    sharedAPIStore?.mainPlayerRef,
    coreStore.content,
    shared
  ]);

  const updateVisionInformation = useCallback(
    data => {
      const status = data.state.split(':')[1] as FaceRecognitionStatusEnum;
      const isFullVideo = !!data.state.includes('FULLVIDEO');

      // BE emits FULLVIDEO events unconditionally, so ignore them when the FF is off
      // (those orgs rely on the per-clip ACCURATE flow instead).
      if (isFullVideo && !isFullVideoFaceRecEnabled()) {
        return;
      }

      // Full-video has a single (accurate) flow — no fast/accurate split.
      const isAnalysisAccurate = isFullVideo || !!data.state.includes('ACCURATE');

      if (!isAnalysisAccurate) {
        return;
      }

      if (!clipData.asset_metadata && (status === 'DONE' || status === 'FAILED')) {
        sessionStorage.setItem(
          `facialRecognitionStatusAndTypeFor:${clipId}`,
          `${status}-${isAnalysisAccurate ? 'true' : 'false'}`
        );
        return;
      }

      const matchesClip = isFullVideo ? data.upload_id === clipBroadcastId : data.id === clipId;

      if (!clipId || !matchesClip || !clipData.asset_metadata) {
        return;
      }

      updateClipAndLayoutStatus(clipId, status, isAnalysisAccurate);
    },
    [layoutStatus, clipId, clipData, clipBroadcastId]
  );

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.ReloadClip, reloadClip);

    return () => {
      EventBus.off(CustomEvents.ReloadClip, eventListener);
    };
  }, []);

  useEffect(() => {
    const onMigrationDone = ({ eventId, broadcastId }: { eventId: string; broadcastId: string }) => {
      if (!clipData?.content) {
        return;
      }
      if (clipData.content.id === broadcastId && clipData.content.project.id === eventId) {
        reloadClip({ showLoader: true, updateCoreStore: true });
      }
    };

    const eventListener = EventBus.on(CustomEvents.EventMigrationCompleted, onMigrationDone);

    return () => {
      EventBus.off(CustomEvents.EventMigrationCompleted, eventListener);
    };
  }, [clipData?.content?.id, clipData?.content?.project?.id]);

  useEffect(() => {
    const facialRecognitionEventListener = EventBus.on(
      CustomEvents.FacialRecognitionStatusUpdated,
      updateVisionInformation
    );

    return () => {
      EventBus.off(CustomEvents.FacialRecognitionStatusUpdated, facialRecognitionEventListener);
    };
  }, [updateVisionInformation]);

  useEffect(() => {
    if (!clipId || loading) {
      return;
    }

    const handleScreenshareDetectionDone = (data: { state: string; broadcast_id?: string }) => {
      if (data.state !== PUSHER_STATES.SCREENSHARE_DETECTION_DONE) {
        return;
      }
      if (data.broadcast_id !== clipData.content?.id) {
        return;
      }
      reloadClip();
    };

    const eventListener = EventBus.on(CustomEvents.ScreenshareDetectionStatusUpdated, handleScreenshareDetectionDone);

    return () => {
      EventBus.off(CustomEvents.ScreenshareDetectionStatusUpdated, eventListener);
    };
  }, [clipId, loading, clipData.content?.id]);

  useEffect(() => {
    if (!clipId || !clipData || loading) {
      return;
    }
    const hasScreenshareBounds = (speakersAnalysis?.screenshare_bounds?.length ?? 0) > 0;
    const shouldShowScreenshareDetectionToast =
      !hasShownScreenshareToast(clipId) &&
      isScreenShareDetectionFeatureFlagEnabled() &&
      speakersAnalysis?.accurate === true &&
      hasScreenshareBounds;

    if (!shouldShowScreenshareDetectionToast) {
      return;
    }

    showSuccessToast(SCREENSHARE_TOAST_MESSAGE);
    markScreenshareToastAsShown(clipId);
  }, [speakersAnalysis, clipData, clipId, loading]);

  const actions = useMemo(
    () => ({ setClipVideosLoading, setIsClipAllowedToPlay }),
    [setClipVideosLoading, setIsClipAllowedToPlay]
  );

  const { status: clipAvailabilityStatus } = useClipAvailabilityStatus(clipId!);

  const fps = useMemo(() => {
    return getClipFPS(clipData);
  }, [clipData]);

  if (clipAvailabilityStatus === ClipAvailabilityStatus.COMPOSITE_MP4_NOT_READY) {
    return <OriginalRecordingGenerationInProgress showLoaderOnPage={showLoaderOnPage} />;
  }

  if (
    clipAvailabilityStatus === ClipAvailabilityStatus.ALIGNED_STREAM_NOT_READY ||
    clipAvailabilityStatus === ClipAvailabilityStatus.RECORDING_STUDIO_UNSUPPORTED_EVENT
  ) {
    return <RSClipFallbackWithoutNewTimeline showLoaderOnPage={showLoaderOnPage} />;
  }

  if (clipAvailabilityStatus === ClipAvailabilityStatus.LIVE_EVENT_MIGRATION_IN_PROGRESS) {
    return <EventMigrationInProgress showLoaderOnPage={showLoaderOnPage} />;
  }

  return loading ? (
    useNewSkeletonLoader ? (
      <div className={'flex size-full items-center justify-center bg-slate-100'}>
        <ClipSkeletonLoader />
      </div>
    ) : (
      <div
        className={`${showLoaderOnPage ? 'fixed inset-0' : ''} flex size-full items-center justify-center bg-white py-2`}
      >
        <Loader />
      </div>
    )
  ) : error && clipId ? (
    renderErrorComponent ? (
      renderErrorComponent(error, clipId)
    ) : (
      <div className="flex size-full select-none items-center justify-center p-2" contentEditable={false}>
        Failed to load clip. Please try again after some time.
      </div>
    )
  ) : clipId && Object.keys(clipData).length ? (
    <ActionsContext.Provider value={actions}>
      <ClipsContext.Provider
        value={{
          clipId,
          clipData,
          clipVideosLoading,
          clipCaptionsLoading,
          speakersAnalysis: speakersAnalysis || defaultSpeakersAnalysis,
          sharedAPIStore,
          playerStore,
          layoutStatus,
          isClipAllowedToPlay,
          fps,
          aiEditsAdapter
        }}
      >
        {children}
      </ClipsContext.Provider>
    </ActionsContext.Provider>
  ) : null;
};

export const useClipsContext = () => {
  const clipsContext = useContext(ClipsContext);
  if (clipsContext === undefined) {
    throw new Error('useClipsContext must be used within a ClipsContextProvider');
  }
  const actionsContext = useContext(ActionsContext);

  const context = useMemo(() => ({ ...clipsContext, ...actionsContext }), [clipsContext, actionsContext]);

  return context;
};
